import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Link} from 'react-router-dom'
import {Alert, Button, Modal, notification, Spin, Table, Tag} from 'antd'
import {createBatchSewingRepairV2, previewBatchSewingRepairV2} from '../../../services/api/3dApi'

// @FAC-25966: Tái tạo lô sửa may — BE chia lô theo đối tác may gốc, tạo thẳng ở Đang may.
// Bản xem trước lấy từ API preview (cùng logic chia lô với API tạo) để user kiểm tra đúng dữ liệu trước khi tạo.

const RESULT_COLUMNS = [
    {
        title: 'Tên lô',
        key: 'name',
        render: row => <Link to={`/batches/${row.batch._id}`} target='_blank'>{row.batch.name}</Link>,
    },
    {
        title: 'Người tạo',
        key: 'user_create',
        render: row => row.batch.user_create,
    },
    {
        title: 'Số item',
        key: 'item_count',
        dataIndex: 'item_count',
    },
    {
        title: 'Trạng thái',
        key: 'status',
        render: row => row.batch.status === 'sewing' ? <Tag color='blue'>Đang may</Tag> : <Tag>{row.batch.status}</Tag>,
    },
]

// "<tên> (<code>)" — dữ liệu partner thật BE tra từ DB
const _partnerLabel = partner => partner ? `${partner.name} (${partner.code})` : '-'

// Gộp item bị loại theo lý do để hiển thị gọn
const _groupExcluded = (excluded) => {
    const byReason = {}

    excluded.forEach(row => {
        // Id rác / không tìm thấy không có barcode → hiển thị order_id
        byReason[row.reason] = (byReason[row.reason] || []).concat(row.barcode ? `#${row.barcode}` : row.order_id)
    })

    return Object.keys(byReason).map(reason => ({reason, barcodes: byReason[reason]}))
}

function SewingRepairBatchModal(props) {
    const {items, selectedBatchId, reset, onResetSelected} = props
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [preview, setPreview] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [created, setCreated] = useState(null)
    // Bỏ qua response preview cũ nếu user đóng/mở lại modal trước khi request trước trả về
    const previewRequestRef = useRef(0)

    // Chỉ tái tạo item đang hiển thị: lựa chọn không bị reset khi đổi trang/filter,
    // gửi cả item ở trang khác sẽ làm kết quả lệch với bản xem trước
    const selectedRows = items.filter(row => row.item && selectedBatchId.includes(row.item._id))
    const selectedOrderIds = selectedRows.map(row => row.item._id)
    const hiddenSelectedCount = selectedBatchId.length - selectedOrderIds.length

    const defaults = preview ? preview.defaults : null
    const groups = preview ? preview.groups : []
    const excludedGroups = preview ? _groupExcluded(preview.excluded) : []
    const createdBatches = created || []
    const createdItems = createdBatches.reduce((sum, row) => sum + row.item_count, 0)
    const canSubmit = !previewLoading && !loading && selectedOrderIds.length > 0 && groups.length > 0

    async function _fetchPreview() {
        const requestId = previewRequestRef.current + 1
        previewRequestRef.current = requestId

        setPreview(null)
        setPreviewLoading(true)

        try {
            const {data: response} = await previewBatchSewingRepairV2({order_ids: selectedOrderIds})
            const {success, error: errorMessage, data} = response

            if (requestId !== previewRequestRef.current) return

            if (!success) {
                setError(errorMessage)

                return
            }

            setPreview(data)
        } catch (e) {
            if (requestId === previewRequestRef.current) setError(e.message)
        } finally {
            if (requestId === previewRequestRef.current) setPreviewLoading(false)
        }
    }

    function _showModal() {
        setError('')
        // Luôn xoá preview cũ: lựa chọn có thể đã đổi (đổi trang/filter) từ lần mở trước
        setPreview(null)
        setIsOpen(true)

        if (selectedOrderIds.length) {
            _fetchPreview()
        }
    }

    function _closeModal() {
        // Không cho đóng khi đang tạo lô — tránh nuốt lỗi, bấm lại lần 2 hoặc mất popup kết quả
        if (loading) return

        previewRequestRef.current += 1
        setIsOpen(false)
        setPreviewLoading(false)
        setError('')
    }

    // Chỉ bỏ chọn khi đóng popup kết quả: header ẩn khối "Thao tác" khi không còn item được chọn,
    // bỏ chọn sớm sẽ unmount component này và mất popup kết quả
    function _closeResult() {
        setCreated(null)
        onResetSelected()
    }

    async function _onSubmit() {
        setLoading(true)
        setError('')

        try {
            const {data: response} = await createBatchSewingRepairV2({order_ids: selectedOrderIds})
            const {success, error: errorMessage, data} = response

            if (!success) {
                setLoading(false)
                setError(errorMessage)

                return
            }

            setLoading(false)
            setIsOpen(false)
            setCreated(data.batches || [])

            if (Array.isArray(data.messageError) && data.messageError.length > 0) {
                notification.warning({
                    message: 'Một số item chưa được tái tạo lô',
                    description: data.messageError.map((item, index) => <p className='mb-0' key={index}>{item}</p>),
                    duration: 0,
                })
            }

            reset()
        } catch (e) {
            setLoading(false)
            setError(e.message)
        }
    }

    return (
        <div className='SewingRepairBatchAction'>
            <Button onClick={_showModal}>
                Tái tạo lô
            </Button>

            <Modal
                visible={isOpen}
                title='Tái tạo lô sửa may'
                wrapClassName='SewingRepairBatchModal'
                width={640}
                onCancel={_closeModal}
                closable={!loading}
                maskClosable={!loading}
                keyboard={!loading}
                footer={[
                    <Button key='back' onClick={_closeModal} disabled={loading}>
                        Hủy
                    </Button>,
                    <Button key='submit' type='primary' loading={loading} disabled={!canSubmit} onClick={_onSubmit}>
                        Tái tạo
                    </Button>,
                ]}
            >
                {
                    error &&
                    <Alert
                        type='error'
                        className='mb-3'
                        message={error}
                        description={
                            !preview && !previewLoading && selectedOrderIds.length > 0 &&
                            <Button size='small' onClick={() => { setError(''); _fetchPreview() }}>Thử lại</Button>
                        }
                    />
                }

                {
                    hiddenSelectedCount > 0 &&
                    <Alert
                        type='warning'
                        className='mb-3'
                        message={`Có ${hiddenSelectedCount} item đã chọn ở trang/bộ lọc khác không nằm trong lần tái tạo này. Chỉ tái tạo các item đang hiển thị trên trang.`}
                    />
                }

                <Alert
                    type='info'
                    className='mb-3'
                    message={<span>Hệ thống tự chia lô theo <strong>đối tác may gốc</strong> của item (đối tác may của lô gần nhất chứa item). Một lô được chứa nhiều SKU và nhiều brand.</span>}
                />

                <Spin spinning={previewLoading} tip='Đang kiểm tra dữ liệu...'>
                    <dl className='FixedInfo'>
                        <dt>Đối tác in ép</dt>
                        <dd>{defaults ? _partnerLabel(defaults.print_partner) : '-'}</dd>
                        <dt>Đối tác may</dt>
                        <dd>{defaults ? _partnerLabel(defaults.sewing_partner) : '-'}</dd>
                        <dt>Độ ưu tiên</dt>
                        <dd>{defaults ? <Tag color='red'>{defaults.level_name}</Tag> : '-'}</dd>
                        <dt>Trạng thái lô</dt>
                        <dd><Tag color='blue'>Đang may</Tag></dd>
                        <dt>Người tạo</dt>
                        <dd>Sửa may - &lt;tên đối tác may gốc&gt;</dd>
                    </dl>

                    {
                        excludedGroups.length > 0 &&
                        <Alert
                            type='warning'
                            className='mb-3 ExcludedList'
                            message={`${preview.excluded.length} item sẽ không được tái tạo lô`}
                            description={excludedGroups.map(row => (
                                <p className='mb-1' key={row.reason}>
                                    <strong>{row.reason}:</strong> {row.barcodes.join(', ')}
                                </p>
                            ))}
                        />
                    }

                    <div className='GroupPreview'>
                        <div className='GroupPreviewTitle'>
                            Dự kiến tạo <strong>{groups.length}</strong> lô
                            {preview && <span> ({preview.total_items} items)</span>}
                        </div>
                        {
                            groups.map(group => (
                                <div className='Group' key={group.key}>
                                    <div className='GroupHeader'>
                                        <strong>{group.user_create}</strong>
                                        <span className='text-muted'>{group.item_count} item · {group.skus.join(', ')}</span>
                                    </div>
                                    <div className='GroupItems'>
                                        {group.items.map(item => <Tag key={item.order_id}>#{item.barcode}</Tag>)}
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </Spin>

                <div className='text-right mt-2'>Đã chọn: <strong>{selectedRows.length}</strong> items</div>
            </Modal>

            <Modal
                visible={!!created}
                title={`Đã tạo ${createdBatches.length} lô sửa may (${createdItems} items)`}
                wrapClassName='SewingRepairBatchModal'
                width={640}
                onCancel={_closeResult}
                footer={[
                    <Button key='close' type='primary' onClick={_closeResult}>
                        Đóng
                    </Button>,
                ]}
            >
                {/* Tạo nhiều lô (vd 15 lô) → bảng dài hơn màn hình: cuộn trong khung, giữ nút Đóng luôn thấy */}
                <div className='ResultTable'>
                    <Table
                        rowKey={row => row.batch._id}
                        dataSource={createdBatches}
                        columns={RESULT_COLUMNS}
                        pagination={false}
                        size='small'
                    />
                </div>
                {
                    defaults &&
                    <p className='mt-2 mb-0 text-muted'>
                        Đối tác in ép: {defaults.print_partner.name} · Đối tác may: {defaults.sewing_partner.name} · Độ ưu tiên: {defaults.level_name}. Bấm tên lô để mở chi tiết lô.
                    </p>
                }
            </Modal>
        </div>
    )
}

SewingRepairBatchModal.propTypes = {
    items: PropTypes.array.isRequired,
    selectedBatchId: PropTypes.array.isRequired,
    reset: PropTypes.func.isRequired,
    onResetSelected: PropTypes.func.isRequired,
}

export default SewingRepairBatchModal
